import React, {useState} from 'react';
import {Box, Typography, Paper, Button,Divider, LinearProgress, TextField, IconButton} from '@mui/material';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { makeStyles } from "@material-ui/core/styles";
import {SketchPicker} from "react-color";
import {uploadMedia} from "../../../services/public.service";
import {buildInsiderAssessmentService} from "../../../services/assessments.service";
import {FormControl, Tab, Tabs} from "@material-ui/core";
import {LANGUAGES_SUPPORTED} from "../../../utils/constants";
import { Restore } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        padding: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    title: {
        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
        color: 'transparent',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: '500 !important',
        textAlign: 'center',
        fontSize: '45px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: "28px !important",
        },
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: theme.spacing(4),
        margin: theme.spacing(2),
        width: '100%',
        maxWidth: '800px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    inputField: {
        marginBottom: theme.spacing(3),
        width: "100%",
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#fff',
            '& fieldset': {
                border: '1px solid #ff6468',
            },
            '&:hover fieldset': {
                border: '1px solid #ff6468',
            },
            '&.Mui-focused fieldset': {
                border: '1px solid #ff6468',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#ff6468',
        },
    },
    colorSection: {
        marginBottom: theme.spacing(4),
        display:'flex',
        [theme.breakpoints.down('sm')]: {
           flexDirection:'column',
            marginTop:'80px',
        },
        [theme.breakpoints.down('md')]: {
           flexDirection:'row',
            marginTop:'80px',
            gap:'15px',
        },
    },
    colorPickerContainer: {
        display: 'flex',
        flexDirection:'column',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        marginLeft:'20px',
        [theme.breakpoints.down('sm')]: {
            marginLeft:'0px',
        },
    },
    colorLabel: {
        minWidth: '120px',
        fontWeight: 'bold',
        color: '#555',
    },
    colorDescription: {
        fontSize: '18px !important',
        lineHeight: '1.1',
        padding: '10px 0px',
        marginBottom: '20px',
        marginLeft: '10px',
        height: '100px',
        textAlign: 'center',
        color: '#000000',
        maxWidth:'600px',
        [theme.breakpoints.down('sm')]: {
        maxWidth:'300px',
        },
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(4),
        gap: theme.spacing(2),
    },
    button: {
        backgroundColor: '#ff6468 !important',
        color: '#ffffff !important',
        fontWeight: 'bold !important',
        fontSize: '16px !important',
        borderRadius: '8px !important',
        padding: '8px 24px !important',
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#ff6468 !important',
            opacity: 0.9,
        },
    },
    cancelButton: {
        borderColor: '#0097b2 !important',
        color: '#0097b2 !important',
        fontWeight: 'bold !important',
        fontSize: '16px !important',
        borderRadius: '8px !important',
        padding: '8px 24px !important',
        textTransform: 'none',
    },
    imageUploadContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: theme.spacing(4),
    },
    assessmentImage: {
        borderRadius: '8px',
        objectFit: 'cover',
        height:'100px',
        border: '1px solid #ddd',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            height:'60px',
        },
    },
}));


const DQProAssessmentDetails = () => {
    const classes = useStyles();
    const location = useLocation();
    const history=useHistory();
    const { code } = useParams();
    const [initialAssessment, setInitialAssessment] = useState(location.state?.assessment || {});

    const [translations, setTranslations] = useState(initialAssessment.translations || {});

    const allLangs = Array.from(new Set([
        'en',
        ...Object.keys(translations || {})
    ]));

    const [currentLanguageTab, setCurrentLanguageTab] = useState('en');

    // Default colors
    const DEFAULT_THEME_COLOR = "#4A90E2";
    const DEFAULT_BADGE_COLOR = "#1A194C";
    const DEFAULT_PRIMARY_COLOR = "#FFFFFF";

    const [fields, setFields] = useState({
        full_name: initialAssessment.full_name || "",
        description: initialAssessment.description || "",
        theme_color: initialAssessment.theme_color || DEFAULT_THEME_COLOR,
        badge_background: initialAssessment.badge_background || DEFAULT_BADGE_COLOR,
        primary_color: initialAssessment.primary_color || DEFAULT_PRIMARY_COLOR,
        image_url: initialAssessment.image_url || ""
    });

    // Preset colors for each picker
    const themeColorPresets = [
        "#4A90E2", "#FF6468", "#ED6628", "#0097B2", "#8C74A7", "#D44C71", "#C91C6E", "#6A8EC9"
    ];
    const badgeColorPresets = [
        "#1A194C", "#000000", "#2C3E50", "#34495E", "#1A237E", "#0D47A1", "#1B5E20", "#4A148C"
    ];
    const primaryColorPresets = [
        "#FFFFFF", "#000000", "#F5F5F5", "#E0E0E0", "#FFFAF0", "#FFF8DC", "#F0F8FF", "#F5FFFA"
    ];

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [editing, setEditing] = useState(false);

    const handleFieldChange = (key, value) => {
        setFields(f => ({ ...f, [key]: value }));
    };

    // Handle name/description field change for translations
    const handleTranslationFieldChange = (lang, key, value) => {
        if (lang === 'en') {
            setFields(f => ({ ...f, [key]: value }));
        } else {
            setTranslations(prev => ({
                ...prev,
                [lang]: {
                    ...(prev[lang] || {}),
                    [key]: value
                }
            }));
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('media', file);

        try {
            const result = await uploadMedia(formData, (progressEvent) => {
                if (progressEvent.total) {
                    setUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                }
            });
            if (result?.data?.media_path) {
                setFields(f => ({
                    ...f,
                    image_url: result.data.media_path
                }));
            }
        } catch (err) {
            alert('Error uploading image!');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        const payload = {
            code: initialAssessment.code,
            full_name: fields.full_name,
            description: fields.description,
            user_group: initialAssessment.user_group,
            indicator_codes: initialAssessment.indicator_codes,
            length_questions: initialAssessment.length_questions,
            time_to_complete_minutes: initialAssessment.time_to_complete_minutes,
            image_url: fields.image_url,
            theme_color: fields.theme_color,
            badge_background: fields.badge_background,
            primary_color: fields.primary_color,
            more_info: initialAssessment.more_info,
            translations: translations
        };

        try {
            const response = await buildInsiderAssessmentService(payload);
            if (response.data) {
                setInitialAssessment(response.data);
                setEditing(false);
            }
        } catch (err) {
            console.error(err);
            // Optionally show an error message to the user
        }
    };

    if (editing) {
        return (
            <Box style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center'}}>
                    <Typography variant="h2" className={classes.title} sx={{ mb: 2, mt:2 }}>
                        Edit Your Assessment</Typography>

                    {/* Image Upload */}
                    <Box className={classes.imageUploadContainer}>
                        <img
                            src={fields.image_url}
                            alt="Assessment"
                            className={classes.assessmentImage}
                        />
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="icon-upload-input"
                            onChange={handleImageChange}
                        />
                        <label htmlFor="icon-upload-input">
                            <Button
                                variant="outlined"
                                component="span"
                                disabled={uploading}
                                className={classes.button}
                            >
                                {uploading ? "Uploading..." : "Change Icon"}
                            </Button>
                        </label>
                        {uploading && (
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                style={{ width: '100%', marginTop: '8px' }}
                            />
                        )}
                    </Box>

                    {/* Assessment Name */}
                <Typography className={classes.colorDescription}>
                    This is the name of assessment that your learners will see.<br/>
                    (Max 60 characters)
                </Typography>

                <Box sx={{width:{md:'60%', lg:'40%'}, mt: 3, borderRadius: '5px', border: '2px solid #ff6468ff' }}>
                    <Tabs
                        value={currentLanguageTab}
                        onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{ mb: 2, backgroundColor: '#ffffff' }}
                    >
                        {allLangs.map((lang) => (
                            <Tab
                                key={lang}
                                label={LANGUAGES_SUPPORTED[lang]}
                                value={lang}
                                style={{
                                    textTransform: 'none',
                                    minWidth: 100,
                                    padding: '8px 16px',
                                    marginRight: 1,
                                    borderBottom: currentLanguageTab === lang ? '4px solid #ED6628' : '4px solid transparent',
                                    color: currentLanguageTab === lang ? '#ED6628' : '#666',
                                    fontWeight: currentLanguageTab === lang ? 'bold' : 'normal',
                                    backgroundColor: currentLanguageTab === lang ? '#FFE2D6' : 'transparent',
                                    borderTopLeftRadius: '6px',
                                    borderTopRightRadius: '6px',
                                }}
                            />
                        ))}
                    </Tabs>

                    <Box sx={{ pt: 3, px:2 }}>
                        {allLangs.map((lang) => (
                            <div key={lang} role="tabpanel" hidden={currentLanguageTab !== lang}>
                                <TextField
                                    value={lang === 'en' ? fields.full_name : (translations[lang]?.name || "")}
                                    onChange={(e) => handleTranslationFieldChange(
                                        lang, lang === 'en' ? 'full_name' : 'name', e.target.value)}
                                    label={`Assessment Name${lang !== 'en' ? ` in ${LANGUAGES_SUPPORTED[lang]}` : ''}`}
                                    className={classes.inputField}
                                    sx={{
                                        marginBottom: '20px',
                                        marginTop: { xs: '30px', sm: '0px' },
                                        width: { xs: '75%', sm: '100%' }
                                    }}
                                />
                                <TextField
                                    value={lang === 'en' ? fields.description : (translations[lang]?.description || "")}
                                    onChange={e => handleTranslationFieldChange(lang, 'description', e.target.value)}
                                    label={`Description${lang !== 'en' ? ` in ${LANGUAGES_SUPPORTED[lang]}` : ''}`}
                                    className={classes.inputField}
                                    multiline
                                    rows={4}
                                    sx={{marginBottom:'20px',marginTop:{xs:'60px', sm:'0px'}, width:{xs:'75%', sm:'100%'}}}
                                />
                            </div>
                        ))}
                    </Box>
                </Box>


                    {/* Color Customization */}
                <Typography variant="h5" style={{ marginBottom: '12px',marginTop:'15px', fontWeight:'bold',color: '#ff6468' }}>
                    Color Customization
                </Typography>
                <Typography className={classes.colorDescription}>
                    Edit your assessment color theme.
                </Typography>

                {/* Preview Strip */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        borderRadius: '10px',
                        height: '80px',
                        overflow: 'hidden',
                        mb: 5,
                        mt: 3,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        position: 'relative',
                        maxWidth: '800px',
                        mx: 'auto',
                    }}
                >
                    {/* Reset Icon Button - Top Right */}
                    <IconButton
                        onClick={() => {
                            handleFieldChange('theme_color', DEFAULT_THEME_COLOR);
                            handleFieldChange('badge_background', DEFAULT_BADGE_COLOR);
                            handleFieldChange('primary_color', DEFAULT_PRIMARY_COLOR);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            zIndex: 10,
                            backgroundColor: 'transparent',
                            color: '#ffff',
                            width: 24,
                            height: 24,
                            padding: 0,
                            '&:hover': {
                                backgroundColor: 'transparent',
                                transform: 'scale(1.1)',
                            },
                        }}
                    >
                        <Restore sx={{ fontSize: 18 }} />
                    </IconButton>

                    {/* Left Icon Section */}
                    <Box
                        sx={{
                            width: '80px',
                            height: '100%',
                            backgroundColor: fields.badge_background,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={fields.image_url || "https://cdn-icons-png.flaticon.com/512/1828/1828884.png"}
                            alt="Assessment Icon"
                            style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'contain',
                                filter: fields.image_url ? 'none' : 'brightness(0) invert(1)',
                            }}
                        />
                    </Box>

                    {/* Title & Theme Color Preview */}
                    <Box
                        sx={{
                            flex: 1,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 3,
                            backgroundColor: fields.theme_color,
                            color: fields.primary_color,
                            fontWeight: 600,
                            fontSize: '20px',
                            textAlign: 'left',
                        }}
                    >
                        <Typography 
                            sx={{ 
                                color: fields.primary_color, 
                                fontWeight: 'bold',
                                fontSize: '22px',
                                textTransform: 'uppercase'
                            }}
                        >
                            {fields.full_name || "Assessment Title"}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: fields.theme_color,
                                    border: '1px solid #ccc',
                                }}
                            />
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: fields.badge_background,
                                    border: '1px solid #ccc',
                                }}
                            />
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: fields.primary_color,
                                    border: '1px solid #ccc',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Color Pickers */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 4,
                        flexWrap: 'wrap',
                        my: 4,
                        maxWidth: '800px',
                        mx: 'auto',
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Theme Color
                        </Typography>
                        <SketchPicker
                            color={fields.theme_color}
                            onChangeComplete={(color) => handleFieldChange('theme_color', color.hex)}
                            presetColors={themeColorPresets}
                            width={180}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Badge Background
                        </Typography>
                        <SketchPicker
                            color={fields.badge_background}
                            onChangeComplete={(color) => handleFieldChange('badge_background', color.hex)}
                            presetColors={badgeColorPresets}
                            width={180}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Primary Color
                        </Typography>
                        <SketchPicker
                            color={fields.primary_color}
                            onChangeComplete={(color) => handleFieldChange('primary_color', color.hex)}
                            presetColors={primaryColorPresets}
                            width={180}
                        />
                    </Box>
                </Box>

                    {/* Action Buttons */}
                    <Box className={classes.buttonContainer}>
                        <Button
                            variant="outlined"
                            className={classes.cancelButton}
                            onClick={() => setEditing(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            className={classes.button}
                            onClick={handleSave}
                        >
                            Save Changes
                        </Button>
                    </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.container} sx={{flexDirection: {xs:'column',sm:'row', md:'column', lg:'row'}, }}>
            {/* Left Card - Assessment Info */}
            <Paper className={classes.card} style={{ maxWidth: '500px', height: '450px' }}>
                <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                    <Box>
                        <Box style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
                            <img
                                src={fields.image_url}
                                alt="Assessment"
                                style={{
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                    border: '1px solid #ddd',
                                    height:'77px',
                                }}
                            />
                        </Box>

                        <Typography variant="h5" style={{
                            fontWeight: 'bold',
                            lineHeight: '1.1',
                            textAlign: 'center',
                            marginTop: '20px',
                            color: '#000000'
                        }}>
                            {fields.full_name}
                        </Typography>

                        <Typography paragraph style={{
                            fontSize: '20px',
                            lineHeight: '1.1',
                            padding: '10px 0px',
                            marginTop: '20px',
                            marginLeft: '10px',
                            height: '100px',
                            textAlign: 'left',
                            color: '#000000'
                        }}>
                            {fields.description}
                        </Typography>
                    </Box>

                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            style={{
                                backgroundColor: '#ff6468',
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                borderRadius: '8px',
                                padding: '8px 24px',
                            }}
                            onClick={() => setEditing(true)}
                        >
                            Edit Info
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Right Card - Preview */}
            <Paper className={classes.card} style={{ maxWidth: '500px', height: '450px' }}>
                <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" style={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                            lineHeight: '1.1',
                            marginTop: '100px',
                            marginBottom: '20px',
                            color: '#000000'
                        }}>
                            Check Your Assessment
                        </Typography>
                        <Typography paragraph style={{
                            fontSize: '20px',
                            lineHeight: '1.1',
                            padding: '10px 0px',
                            marginTop: '10px',
                            marginLeft: '10px',
                            height: '100px',
                            textAlign: 'left',
                            color: '#000000'
                        }}>
                            Here is the link to preview your created assessment to see what your clients and learners see.
                        </Typography>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
                        <Button
                            onClick={() =>
                                history.push({
                                    pathname: `/dq_pro/product/view/${code}/preview`,
                                    state: { assessment: fields },
                                })
                            }
                            variant="contained"
                            style={{
                                backgroundColor: '#ff6468',
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                borderRadius: '8px',
                                padding: '8px 24px',
                                maxWidth: '200px'
                            }}
                        >
                            Preview
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default DQProAssessmentDetails;